import { useTranslation } from 'react-i18next';
import {
  CONTENT_LIBRARY_FILTERS,
  CONTENT_TILE_SIZE_MAX,
  CONTENT_TILE_SIZE_MIN,
  type ContentLibraryFilter,
} from './buildContentLibrary';

type ContentLibraryToolbarProps = {
  filter: ContentLibraryFilter;
  tileSize: number;
  onFilterChange: (filter: ContentLibraryFilter) => void;
  onTileSizeChange: (size: number) => void;
};

export function ContentLibraryToolbar({
  filter,
  tileSize,
  onFilterChange,
  onTileSizeChange,
}: ContentLibraryToolbarProps) {
  const { t } = useTranslation('content');
  return (
    <div className="flex flex-wrap items-center gap-x-[18px] gap-y-3">
      <span
        className="text-[18px] font-extrabold tracking-[-0.02em]"
        style={{ color: 'var(--gs-ink)' }}
      >
        {t('library.title')}
      </span>

      <span
        className="flex gap-0.5 rounded-[10px] border p-[3px]"
        style={{
          background: 'var(--gs-panel2)',
          borderColor: 'var(--gs-line)',
        }}
        role="tablist"
        aria-label={t('library.filterLabel')}
      >
        {CONTENT_LIBRARY_FILTERS.map((id) => {
          const active = filter === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onFilterChange(id)}
              className="rounded-[7px] px-[13px] py-[7px] text-[13px] font-bold"
              style={{
                color: active ? 'var(--gs-ink)' : 'var(--gs-muted)',
                background: active ? 'var(--gs-panel)' : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.18)' : undefined,
              }}
            >
              {t(`library.filters.${id}`)}
            </button>
          );
        })}
      </span>

      <span
        className="ms-auto flex items-center gap-2.5"
        style={{ color: 'var(--gs-muted)' }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="4" y="4" width="7" height="7" rx="1" />
          <rect x="13" y="4" width="7" height="7" rx="1" />
          <rect x="4" y="13" width="7" height="7" rx="1" />
          <rect x="13" y="13" width="7" height="7" rx="1" />
        </svg>
        <input
          type="range"
          min={CONTENT_TILE_SIZE_MIN}
          max={CONTENT_TILE_SIZE_MAX}
          step={10}
          value={tileSize}
          onChange={(event) => onTileSizeChange(Number(event.target.value))}
          aria-label={t('library.thumbnailSize')}
          className="w-[120px]"
          style={{ accentColor: '#C2551C' }}
        />
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      </span>
    </div>
  );
}
